import React, {useCallback, useEffect, useState} from 'react';
import {Button, Divider, Form, Input, Popconfirm, Select} from 'antd';
import {useDispatch, useIntl, useSelector} from 'umi';
import {BranchModel} from '@/typings/models/Location';
import {createBranchRequest, deleteBranchRequest, updateBranchRequest} from '@/services/api/location';
import {processAPIResponseNow} from '@/utils/request';
import * as JourneySelectors from '@/selectors/journeySelectors';
import * as LocationSelectors from '@/selectors/locationSelectors';
import * as LeadSelectors from '@/selectors/leadSelectors';
import OfferingSelect from '@/components/Filters/OfferingSelect';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import styles from './index.less';
import CopyableId from '@/components/CopyableId';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import {CheckAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import {DeleteOutlined} from '@ant-design/icons';
import MapWithAutocomplete from '@/components/AutoComplete/MapWithAutocomplete';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import {updateExtraFieldsArray} from '@/utils/extraFields';
import NavLabel from '@/components/NavLabel';
import VirtualUserList from '@/components/UserList/VirtualUserList';

interface BranchDetailsFormProps {
  branch: BranchModel;
  onSuccess?: (branch: BranchModel) => void;
}

const mapIdsToObjects = (
  changedValues: any,
  offerings: any[],
  journeys: any[],
) => {
  const reduxData = { ...changedValues };
  if ('offering_id' in changedValues) {
    reduxData.offering = offerings.find((o) => o.uuid === changedValues.offering_id) || null;
    delete reduxData.offering_id;
  }
  if ('lead_source_id' in changedValues) {
    const journey = journeys.find((j) => j.hashId === changedValues.lead_source_id);
    reduxData.lead_source = journey ? { id: journey.hashId, name: journey.name } : null;
    delete reduxData.lead_source_id;
  }
  return reduxData;
};

const BranchDetailsForm: React.FC<BranchDetailsFormProps> = ({ branch, onSuccess }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const branchDrawerVisible = useSelector(
    LocationSelectors.selectBranchDrawerVisible,
  );
  if (!branch) return null;

  const isNew = branch.id === 'NEW';

  const journeys = useSelector(JourneySelectors.selectJourneys);
  const offerings = useSelector(LocationSelectors.selectOfferingList);
  const allAgents = useSelector(LeadSelectors.selectAllAgentsList);

  const toggleBranchDrawer = () => {
    dispatch({
      type: 'location/toggleBranchDrawer',
    });
  };

  useEffect(() => {
    form.resetFields()
  }, [branchDrawerVisible]);

  useEffect(() => {
    if (branch?.id && branch?.id !== 'NEW') {
      form.setFieldsValue({
        ...branch,
        offering_id: branch.offering?.uuid,
        lead_source_id: branch.lead_source?.id,
        staff: branch.staff?.map(s => typeof s === 'string' ? s : s.id),
      });
    }
  }, [branch, form]);

  const handlePatch = useCallback(async (changedValues: any) => {
    // Check if anything actually changed
    const hasChanges = Object.keys(changedValues).some(key => {
      let currentValue = branch[key];
      let newValue = changedValues[key];

      // Handle specific mappings
      if (key === 'offering_id') currentValue = branch.offering?.uuid;
      if (key === 'lead_source_id') currentValue = branch.lead_source?.id;
      if (key === 'staff') {
        currentValue = branch.staff?.map(s => typeof s === 'string' ? s : s.id);
      }

      return !isEqual(currentValue, newValue);
    });

    if (!hasChanges) return;

    Object.keys(changedValues).forEach((key) => {
      if (changedValues[key] === undefined) changedValues[key] = null;
    });

    const reduxData = mapIdsToObjects(changedValues, offerings, journeys);

    dispatch({
      type: 'location/setBranchDetail',
      payload: {
        branchId: branch.id!,
        branchData: reduxData,
      },
    });

    form.setFieldsValue(changedValues);

    if (isNew) return;

    setLoading(true);
    if (reduxData.staffId){
      changedValues.staff = changedValues.staffId;
      delete changedValues.staffId;
    }
    const apiResponse = await updateBranchRequest({
      params: { branchId: branch.id! },
      payload: changedValues,
    });

    processAPIResponseNow(apiResponse, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.BRANCH,
      onSuccess: (data) => {
        const updatedBranch = data?.data || { ...branch, ...reduxData };

        dispatch({
          type: 'location/setBranchDetail',
          payload: {
            branchId: branch.id!,
            branchData: updatedBranch,
          },
        });
        onSuccess?.(updatedBranch);
      },
      onEnd: () => setLoading(false),
    });
  }, [branch, isNew, form, dispatch, onSuccess, offerings, journeys]);

  const deleteBranch = () => {
    return deleteBranchRequest({
      params: {
        branchId: branch.id,
      },
    }).then(toggleBranchDrawer);
  };
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      values.is_active = true;
      const apiResponse = await createBranchRequest({
        payload: values,
      });

      processAPIResponseNow(apiResponse, {
        onSuccess: (data) => {
          const newBranch = data?.data || data?.branch || data;
          dispatch({
            type: 'location/setBranchDetail',
            payload: {
              branchId: newBranch.id,
              branchData: newBranch,
            },
          });
          dispatch({ type: 'location/getManageableBranches' });
          onSuccess?.(newBranch);
        },
        onEnd: () => setLoading(false),
      });
    } catch (error: any) {
      setLoading(false);
      console.error('Failed to create branch:', error);
    }
  };

  const handleAddressChange = useCallback((addressData: any) => {
    handlePatch(addressData);
  }, [handlePatch]);

  const handleExtraFieldsChange = useCallback((values: any) => {
    if (!branch?.extra_fields) return;
    const updatedExtraFields = updateExtraFieldsArray(branch.extra_fields as any, values);
    dispatch({
      type: 'location/setBranchDetail',
      payload: {
        branchId: branch.id!,
        branchData: {
          extra_fields: updatedExtraFields,
        },
      },
    });
  }, [branch, dispatch]);

  const submitOnEnter = useSubmitOnEnter(isNew ? handleCreate : undefined, {
    enabled: !loading,
  });

  return (
    <div className={styles.branchDetailsForm}>
      <Form
        {...submitOnEnter}
        form={form}
        layout="vertical"
        initialValues={{ is_active: true }}
      >
        <NavLabel strong size={'large'} noPadding>
          <F id="common.general" defaultMessage="General" />
        </NavLabel>
        {!isNew && (
          <Form.Item
            name="is_active"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <ToggleCard
              title={
                <F
                  id="pages.locations.branchStatus"
                  defaultMessage="Branch Status"
                />
              }
              description={
                <F
                  id="pages.locations.branchStatus.description"
                  defaultMessage="Controls if the branch is active and visible to customers"
                />
              }
              onChange={(checked) => handlePatch({ is_active: checked })}
            />
          </Form.Item>
        )}

        <Form.Item
          label={<F id="common.attribute.name" defaultMessage="Branch Name" />}
          name="name"
          rules={[{ required: true }]}
        >
          <Input
            placeholder={intl.formatMessage({
              id: 'pages.locations.branchName.placeholder',
              defaultMessage: 'Enter branch name',
            })}
            onBlur={(e) => handlePatch({ name: e.target.value })}
          />
        </Form.Item>

        <Form.Item
          label={
            <F
              id="pages.leads.filters.offering.placeholder"
              defaultMessage="Category"
            />
          }
          hidden={isNew}
          name="offering_id"
        >
          <OfferingSelect
            filterMode={'select'}
            onChange={(value) => handlePatch({ offering_id: value })}
          />
        </Form.Item>

        <Form.Item
          label={
            <F id="pages.locations.locationManager" defaultMessage="Branch Manager" />
          }
          hidden={isNew}
          name="staff"
          tooltip={intl.formatMessage({
            id: 'pages.locations.locationManagerTooltip',
          })}
        >
          <VirtualUserList
            users={(branch.staff || []).map(s => {
              if (typeof s === 'string') {
                return allAgents.find(a => a.id === s) || { id: s };
              }
              return s;
            }) as any}
            onAdd={(id, user) => {
              const currentStaff = branch.staff || [];
              const currentStaffIds = currentStaff.map((s) => (typeof s === 'string' ? s : s.id));
              if (!currentStaffIds.includes(id)) {
                handlePatch({ staff: [...currentStaff, user], staffId: [...currentStaffIds, id] });
              }
            }}
            onRemove={(id) => {
              const staff = (branch.staff || []).filter((s) => (typeof s === 'string' ? s !== id : s.id !== id)) as any;
              const staffId = staff.map((s) => (typeof s === 'string' ? s : s.id)) || [];
              handlePatch({ staff: staff, staffId: staffId });
            }}
          />
        </Form.Item>

        <Form.Item
          hidden={isEmpty(journeys)}
          label={
            <F
              id="pages.leads.filters.leadSource.placeholder"
              defaultMessage="Lead Source"
            />
          }
          name="lead_source_id"
        >
          <Select
            placeholder={intl.formatMessage({
              id: 'pages.locations.leadSource.selectPlaceholder',
            })}
            options={journeys.map((j) => ({ label: j.name, value: j.hashId }))}
            onChange={(value) => handlePatch({ lead_source_id: value })}
            showSearch
            allowClear
          />
        </Form.Item>

        <NavLabel strong size={'large'} noPadding>
          <F
            id="common.attribute.address"
            defaultMessage="Address & Location"
          />
        </NavLabel>

        <Form.Item name="latitude" noStyle>
          <Input hidden />
        </Form.Item>
        <Form.Item name="longitude" noStyle>
          <Input hidden />
        </Form.Item>
        <Form.Item name="timezone" noStyle>
          <Input hidden />
        </Form.Item>
        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.address !== currentValues.address ||
            prevValues.latitude !== currentValues.latitude ||
            prevValues.longitude !== currentValues.longitude ||
            prevValues.timezone !== currentValues.timezone
          }
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="address"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'component.mapWithAutoComplete.addressPlaceHolder',
                    defaultMessage: 'Please select an address from the map',
                  }),
                },
              ]}
            >
              <MapWithAutocomplete
                height={200}
                place={{
                  address: getFieldValue('address') || (!isNew ? branch.address: ''),
                  latitude:
                    getFieldValue('latitude') ||
                    (!isNew && branch.latitude
                      ? parseFloat(branch.latitude as any)
                      : undefined),
                  longitude:
                    getFieldValue('longitude') ||
                    (!isNew && branch.longitude
                      ? parseFloat(branch.longitude as any)
                      : undefined),
                  timezone: getFieldValue('timezone') || (!isNew ? branch.timezone : undefined),
                }}
                onChange={handleAddressChange}
                hasTimeZones
              />
            </Form.Item>
          )}
        </Form.Item>
      </Form>
      {!isNew && (branch as any)?.extra_fields?.length > 0 && (
        <>
          <NavLabel strong size={'large'} noPadding>
            <F id="entity.extra_field.plural" defaultMessage="Extra Fields" />
          </NavLabel>
          <DynamicExtraFieldsForm
            labelWidth={250}
            fields={(branch as any).extra_fields || []}
            branchId={branch?.id}
            onValuesSubmit={handleExtraFieldsChange}
          />
        </>
      )}
      {isNew ? (
        <div className={styles.formActions}>
          <Button
            type="primary"
            onClick={handleCreate}
            loading={loading}
            block
            size="large"
          >
            <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.CREATE} />
          </Button>
        </div>
      ) : (
        <>
          <CopyableId id={branch?.id} />
          <Divider size={'small'} />
          <CheckAccess
            access={[
              PERMISSIONS.BranchServices.DELETE,
              PERMISSIONS.BUSINESS_PARTNER,
            ]}
          >
            <Popconfirm
              placement="top"
              onConfirm={deleteBranch}
              title={
                <F
                  entity={INTL_ENTITY.BRANCH}
                  action={INTL_ACTION.REMOVE_CONFIRMATION}
                />
              }
            >
              <Button
                color="danger"
                variant="filled"
                block
                icon={<DeleteOutlined />}
              >
                <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.REMOVE} />
              </Button>
            </Popconfirm>
          </CheckAccess>
        </>
      )}
    </div>
  );
};

export default BranchDetailsForm;
